import { Injectable, inject } from '@angular/core'
import { ApiClient, ApiClientFactory, serializeHttpParams } from '@ftr/api-shared'
import { PublicConfiguration } from '@ftr/contracts/api/configuration'
import {
  CourtSystemWithFeaturesResponse,
  CourtSystemWithFeaturesResult,
  ListForCourtSystemPickerQuery,
} from '@ftr/contracts/api/court-system'
import {
  Department,
  DepartmentByCourtSystem,
  DepartmentCourtroomResponse,
  DepartmentOverviewListResponse,
} from '@ftr/contracts/api/department'
import { Location, LocationDetailsResponse, LocationsWithSummaryResponse } from '@ftr/contracts/api/location'
import { VocabularyTerms } from '@ftr/contracts/type/core'
import { LineItemType } from '@ftr/contracts/type/order'
import { Uuid } from '@ftr/contracts/type/shared'
import { CourthouseExtraInformation, CourtroomExtraInformation } from '@ftr/data-location'
import { ApiResult } from '@ftr/foundation'

export interface LocationWithExtraInfo extends Pick<
  Location,
  'id' | 'name' | 'description' | 'imageUrl' | 'status' | 'courtSystemId' | 'addressDetails' | 'parentId' | 'timeZoneId'
> {
  summary?: CourthouseExtraInformation | CourtroomExtraInformation
}

@Injectable()
export class PublicRequestService {
  private readonly apiClient: ApiClient

  constructor() {
    const apiClientFactory = inject(ApiClientFactory)
    this.apiClient = apiClientFactory.build('/public/request')
  }

  listCourtSystems(lineItemType: LineItemType): ApiResult<CourtSystemWithFeaturesResponse> {
    return this.apiClient.get<CourtSystemWithFeaturesResponse>({
      path: '/court-systems',
      params: serializeHttpParams(new ListForCourtSystemPickerQuery(lineItemType)),
      skipAuthentication: true,
    })
  }

  getCourtSystem(courtSystemId: Uuid, lineItemType: LineItemType): ApiResult<CourtSystemWithFeaturesResult> {
    return this.apiClient.get<CourtSystemWithFeaturesResult>({
      path: `/court-systems/${courtSystemId}`,
      params: serializeHttpParams(new ListForCourtSystemPickerQuery(lineItemType)),
      skipAuthentication: true,
    })
  }

  getConfiguration(courtSystemId: Uuid): ApiResult<PublicConfiguration> {
    return this.apiClient.get<PublicConfiguration>({
      path: `/court-systems/${courtSystemId}/configuration`,
      skipAuthentication: true,
    })
  }

  getVocabularyTerms(courtSystemId: Uuid): ApiResult<VocabularyTerms> {
    return this.apiClient.get<VocabularyTerms>({
      path: `/court-systems/${courtSystemId}/vocabulary-terms`,
      skipAuthentication: true,
    })
  }

  listDepartments(courtSystemId: Uuid): ApiResult<Department[]> {
    return this.apiClient.get<Department[]>({
      path: `/court-systems/${courtSystemId}/departments`,
      skipAuthentication: true,
    })
  }

  listCourthousesWithSummary(courtSystemId: Uuid, lineItemType: LineItemType): ApiResult<LocationsWithSummaryResponse> {
    return this.apiClient.get<LocationsWithSummaryResponse>({
      path: `/court-systems/${courtSystemId}/courthouses-with-summary`,
      params: serializeHttpParams(new ListForCourtSystemPickerQuery(lineItemType)),
      skipAuthentication: true,
    })
  }

  listDepartmentOverviews(courtSystemId: Uuid, lineItemType: LineItemType): ApiResult<DepartmentOverviewListResponse> {
    return this.apiClient.get<DepartmentOverviewListResponse>({
      path: `/court-systems/${courtSystemId}/department-overview`,
      params: serializeHttpParams(new ListForCourtSystemPickerQuery(lineItemType)),
      skipAuthentication: true,
    })
  }

  getLocation(courtSystemId: Uuid, locationId: Uuid, lineItemType: LineItemType): ApiResult<LocationDetailsResponse> {
    return this.apiClient.get<LocationDetailsResponse>({
      path: `/court-systems/${courtSystemId}/locations/${locationId}`,
      params: serializeHttpParams(new ListForCourtSystemPickerQuery(lineItemType)),
      skipAuthentication: true,
    })
  }

  listCourtroomsWithSummary(
    courtSystemId: Uuid,
    courthouseId: Uuid,
    lineItemType: LineItemType,
  ): ApiResult<LocationsWithSummaryResponse> {
    return this.apiClient.get<LocationsWithSummaryResponse>({
      path: `/court-systems/${courtSystemId}/courthouses/${courthouseId}/courtrooms-with-summary`,
      params: serializeHttpParams(new ListForCourtSystemPickerQuery(lineItemType)),
      skipAuthentication: true,
    })
  }

  listCourtroomsWithSummaryByDepartment(
    courtSystemId: Uuid,
    departmentId: Uuid,
    lineItemType: LineItemType,
  ): ApiResult<DepartmentCourtroomResponse> {
    return this.apiClient.get<DepartmentCourtroomResponse>({
      path: `/court-systems/${courtSystemId}/departments/${departmentId}/courtrooms-with-summary`,
      params: serializeHttpParams(new ListForCourtSystemPickerQuery(lineItemType)),
      skipAuthentication: true,
    })
  }

  getDepartment(
    courtSystemId: Uuid,
    departmentId: Uuid,
    lineItemType: LineItemType,
  ): ApiResult<DepartmentByCourtSystem> {
    return this.apiClient.get<DepartmentByCourtSystem>({
      path: `/court-systems/${courtSystemId}/departments/${departmentId}`,
      params: serializeHttpParams(new ListForCourtSystemPickerQuery(lineItemType)),
      skipAuthentication: true,
    })
  }
}
